import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { FieldState, ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./aiFieldProposalForm";
import { AiProposalParse } from "./bindAiProposal";

/**
 * One entity field plus how to coerce its proposal string into that field's type.
 * `T` is erased in mixed arrays (`AiFieldBinding[]`); use `aiFieldBinding` so each entry keeps field and parse aligned.
 */
export type AiFieldBinding<T = any> = {
  field: FieldState<T>;
  parse: AiProposalParse<T>;
  proposal?: ObjectState<AiFieldProposalFormValue>;
};

export function aiFieldBinding<T>(
  field: FieldState<T>,
  parse: AiProposalParse<NoInfer<T>>,
  proposal?: ObjectState<AiFieldProposalFormValue>,
): AiFieldBinding<T> {
  return { field, parse, proposal };
}

/**
 * Accept All / Ignore All for the bindings you pass.
 * Accepted: parse `fieldValue`, `field.set` it, mark Accepted. Skip if unparseable (stay Pending).
 * Rejected: mark Rejected only. Does not call a mutation.
 */
export function reconcilePendingAiProposals(
  bindings: AiFieldBinding[],
  decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected,
): void {
  for (const { field, parse, proposal } of bindings) {
    // No proposal on this field, or already Accepted/Rejected.
    if (proposal?.status.value !== AiFieldProposalStatus.Pending) continue;
    if (decision === AiFieldProposalStatus.Accepted) {
      // Coerce GraphQL's string `fieldValue` into the entity field's type.
      const proposedValue = parse(proposal.fieldValue.value);
      // Unparseable (empty or junk) — not "clear the field". Leave Pending.
      if (proposedValue === undefined) continue;
      field.set(proposedValue);
    }
    proposal.status.set(decision);
  }
}
